import { useCallback } from 'react'

import { toast } from '@webapp/components/Notification'
import { getMessageFromError } from '@webapp/core/utils/errors/getMessageFromError'
import { useProcurifyIntl } from '@webapp/hooks'
import { type POPDFLabelSetNameFormSchema } from '@webapp/modules/purchasing/components/POPDFLabelSetNameForm'
import { useMutateRenamePOPDFLabelSet } from '@webapp/modules/purchasing/features/rename-po-pdf-label-set/models'
import { type PDFLabelSet } from '@webapp/modules/vendor/entities/PDFLabelSet' // eslint-disable-line import/no-internal-modules
import messages from './messages'

export interface UseRenamePOPDFLabelSetFormPresenterParams {
  labelSet: PDFLabelSet
  onSuccess: (set: PDFLabelSet) => void
}

export const useRenamePOPDFLabelSetFormPresenter = ({
  labelSet,
  onSuccess,
}: UseRenamePOPDFLabelSetFormPresenterParams) => {
  const intl = useProcurifyIntl()
  const { mutateAsync, isLoading } = useMutateRenamePOPDFLabelSet()

  const handleSubmit = useCallback(
    async (values: POPDFLabelSetNameFormSchema) => {
      try {
        const updatedSet = await mutateAsync({
          labelSet,
          name: values.name,
        })

        toast.success({
          title: intl.formatMessage(messages.successTitle),
          children: intl.formatMessage(messages.successMessage, {
            name: values.name,
          }),
        })

        onSuccess(updatedSet)
      } catch (error) {
        // API failures surface `errors.key.message` (BHV-042); the Feature
        // Set keeps the modal open because onSuccess is never called
        const errorMessage =
          getMessageFromError(error) ||
          intl.formatMessage(messages.defaultErrorMessage)

        toast.error({
          title: intl.formatMessage(messages.errorTitle),
          children: errorMessage,
        })
      }
    },
    [intl, labelSet, mutateAsync, onSuccess]
  )

  return {
    states: {
      isSaving: isLoading,
    },
    handlers: {
      handleSubmit,
    },
  }
}
